import { AxiosError } from 'axios';
import { types, flow, Instance } from 'mobx-state-tree';
import axios from 'utils/axios.config';
import { withAlertStore } from 'components/Core/CustomAlert/store';
import { ErrorData, IResponse, TranslatesObject } from 'types/common';
import { IScriptRef, IScriptStatusReq, IScriptTranslationsReq, IScriptsData, IScriptsList } from './types';

const { model, frozen, maybeNull } = types;

export const ScriptsStore = model('ScriptsStore', {
  scriptsList: maybeNull(frozen<IScriptsList[]>()),
  scriptData: maybeNull(frozen<IScriptsData>()),
})
  .extend(withAlertStore)
  .actions((self) => ({
    setScriptsList(data: IScriptsList[] | null) {
      self.scriptsList = data;
    },
    setScriptData(data: IScriptsData | null) {
      self.scriptData = data;
    },
  }))
  .actions((self) => ({
    errorHandling({ response }: AxiosError<IResponse<ErrorData>>) {
      const msg = response?.data.message || 'Internal server error';
      self.alertStore.showAlert(msg);
      throw new Error(msg);
    },
  }))
  .actions((self) => ({
    getScriptsList: flow(function* () {
      yield axios
        .get('script/index')
        .then(({ data: { data } }) => self.setScriptsList(data))
        .catch(self.errorHandling);
    }),
    changeScriptStatus: flow(function* ({ id, type, status }: IScriptStatusReq) {
      yield axios
        .patch(`script/${type}/status/${id}`, { status })
        .then(() => {
          if (self.scriptsList) {
            self.setScriptsList(self.scriptsList.map((item) => (item.id === id ? { ...item, status } : item)));
          }
        })
        .catch(self.errorHandling);
      return id;
    }),
    resyncScript: flow(function* ({ id, type }: IScriptRef) {
      yield axios
        .get(`script/${type}/resync/${id}`)
        .then(({ data: { data } }) =>
          self.setScriptsList(self.scriptsList?.map((item) => (item.id === id ? data : item)) || [])
        )
        .catch(self.errorHandling);
      return id;
    }),
    getScriptsData: flow(function* ({ id, type }: IScriptRef) {
      yield axios
        .get(`script/${type}/${id}`)
        .then(({ data: { data } }) => self.setScriptData(data))
        .catch(self.errorHandling);
    }),
    changeScriptMainData: flow(function* (payload: IScriptsList) {
      return yield axios
        .patch(`script/${payload.type}/${payload.id}`, payload)
        .then(({ data: { data } }) => {
          if (self.scriptsList) {
            self.setScriptsList(self.scriptsList.map((item) => (item.id === payload.id ? data : item)));
          }
          return data as IScriptsList;
        })
        .catch(self.errorHandling);
    }),
    changeScriptMessageTranslations: flow(function* ({ type, translations }: IScriptTranslationsReq) {
      return yield axios
        .patch(`script/${type}/translations`, { translations })
        .then(({ data: { data } }) => data as TranslatesObject)
        .catch(self.errorHandling);
    }),
  }));

export type IScriptsStore = Instance<typeof ScriptsStore>;
